/**
 * Stream Handler Utilities
 *
 * Factory functions and utilities for handling streaming responses in the chat store.
 * These handlers manage timeouts, deep research updates, and message source fetching.
 */

import type {
  ChatMessage,
  DeepResearchContext,
  DeepResearchUpdate,
  ThinkingStep,
} from '../../types';
import type { WebSearchSource } from '../../../../shared/types/api.types';
import { chatService } from '../../services/ChatService';
import { currentDate, now } from '@/shared/utils/dateUtils';

/**
 * Stream timeout configuration options
 */
export interface StreamTimeoutConfig {
  /** Timeout duration in milliseconds */
  timeout: number;
  /** Whether web search is enabled (affects error messages) */
  isWebSearchEnabled: boolean;
  /** Error message to show for web search timeout */
  webSearchTimeoutMessage?: string;
  /** Error message to show for regular timeout */
  defaultTimeoutMessage?: string;
}

/**
 * Stream timeout manager for tracking per-message timeouts
 * This prevents cross-stream contamination when multiple messages are in flight
 */
export interface StreamTimeoutManager {
  /** Map of message IDs to their timeout handles */
  timeouts: Map<string, NodeJS.Timeout>;
  /** Map of message IDs to their completion status */
  completedFlags: Map<string, boolean>;
}

/**
 * Creates a new stream timeout manager instance
 */
export const createStreamTimeoutManager = (): StreamTimeoutManager => ({
  timeouts: new Map(),
  completedFlags: new Map(),
});

/**
 * Clears all timeouts and flags from a timeout manager
 */
export const clearAllStreamTimeouts = (manager: StreamTimeoutManager): void => {
  manager.timeouts.forEach(timeout => clearTimeout(timeout));
  manager.timeouts.clear();
  manager.completedFlags.clear();
};

/**
 * Marks a stream as completed and clears its timeout
 */
export const markStreamCompleted = (
  manager: StreamTimeoutManager,
  messageId: string
): void => {
  manager.completedFlags.set(messageId, true);
  const timeout = manager.timeouts.get(messageId);
  if (timeout) {
    clearTimeout(timeout);
    manager.timeouts.delete(messageId);
  }
};

/**
 * Clears an existing timeout for a message (used when resetting on chunk arrival)
 */
export const clearMessageTimeout = (
  manager: StreamTimeoutManager,
  messageId: string
): void => {
  const timeout = manager.timeouts.get(messageId);
  if (timeout) {
    clearTimeout(timeout);
    manager.timeouts.delete(messageId);
  }
};

/**
 * Parameters for creating a stream timeout handler
 */
export interface CreateStreamTimeoutParams {
  manager: StreamTimeoutManager;
  messageId: string;
  config: StreamTimeoutConfig;
  /** Callback to get current messages state */
  getMessages: () => ChatMessage[];
  /** Callback to update state when timeout fires */
  onTimeout: (messageId: string, errorMessage: string) => void;
}

/**
 * Creates a stream timeout handler for a specific message.
 * Returns a function that sets up the timeout, which can be called to reset it.
 *
 * @param params - Configuration for the timeout handler
 * @returns A function that sets up/resets the timeout
 */
export const createStreamTimeoutHandler = ({
  manager,
  messageId,
  config,
  getMessages,
  onTimeout,
}: CreateStreamTimeoutParams): (() => void) => {
  const webSearchNoResultsMsg =
    config.webSearchTimeoutMessage ||
    'No web results found. Try a different query.';
  const defaultTimeoutMsg =
    config.defaultTimeoutMessage || 'Stream interrupted. Response timed out.';

  return () => {
    // Clear any existing timeout for this specific message
    const existingTimeout = manager.timeouts.get(messageId);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
      manager.timeouts.delete(messageId);
    }

    const timeout = setTimeout(() => {
      // Check if this specific stream actually completed
      const isCompleted = manager.completedFlags.get(messageId) === true;

      // Only show error if stream didn't complete
      if (!isCompleted) {
        // Double-check message is still in sending state
        const messages = getMessages();
        const message = messages.find(m => m.id === messageId);

        if (message?.status === 'sending') {
          const errorMessage = config.isWebSearchEnabled
            ? webSearchNoResultsMsg
            : defaultTimeoutMsg;
          onTimeout(messageId, errorMessage);
        }
      }

      // Cleanup
      manager.timeouts.delete(messageId);
      manager.completedFlags.delete(messageId);
    }, config.timeout);

    manager.timeouts.set(messageId, timeout);
  };
};

/**
 * Parameters for updating message sources after streaming
 */
export interface UpdateMessageSourcesParams {
  conversationId: string | null;
  aiMessageId: string;
  /** Callback to update messages with sources */
  updateMessages: (updater: (messages: ChatMessage[]) => ChatMessage[]) => void;
}

/**
 * Fetches and updates message sources after streaming completes.
 * This retrieves the additional_kwargs (includes web search sources, RAG sources, etc.)
 * from the conversation history.
 *
 * @param params - Configuration for updating sources
 */
export const updateMessageSources = ({
  conversationId,
  aiMessageId,
  updateMessages,
}: UpdateMessageSourcesParams): void => {
  if (!conversationId) return;

  chatService
    .getConversationHistory(conversationId)
    .then(messages => {
      // Get the last AI message from history which should have sources
      const lastAiMessage = messages.filter(msg => msg.type === 'ai').pop();

      if (
        lastAiMessage?.data.additional_kwargs &&
        Object.keys(lastAiMessage.data.additional_kwargs).length > 0
      ) {
        // Update the AI message with additional_kwargs (includes sources)
        // IMPORTANT: Preserve the status field to ensure action buttons remain visible
        updateMessages(stateMessages =>
          stateMessages.map(msg => {
            if (msg.id === aiMessageId) {
              const currentMessage = stateMessages.find(
                m => m.id === aiMessageId
              );
              return {
                ...msg,
                additional_kwargs: lastAiMessage.data.additional_kwargs,
                // Preserve status from current state (should be 'sent' after stream completes)
                status: currentMessage?.status || msg.status || 'sent',
                // Preserve webSearchSources if already set from stream
                webSearchSources:
                  currentMessage?.webSearchSources || msg.webSearchSources,
              };
            }
            return msg;
          })
        );
      }
    })
    .catch(_error => {
      // Non-critical: source fetching failure doesn't affect chat
    });
};

/**
 * Parameters for creating a deep research update handler
 */
export interface CreateDeepResearchHandlerParams {
  messageId: string;
  /** Callback to check if stream has been cancelled */
  isStreamCancelled: () => boolean;
  /** Callback to update messages with deep research context */
  updateMessages: (updater: (messages: ChatMessage[]) => ChatMessage[]) => void;
}

/**
 * Creates a handler for deep research update events.
 * This handler processes start, update, web_sources, and complete events
 * and updates the message's deepResearchContext accordingly.
 *
 * @param params - Configuration for the handler
 * @returns A function that processes DeepResearchUpdate events
 */
export const createDeepResearchUpdateHandler = ({
  messageId,
  isStreamCancelled,
  updateMessages,
}: CreateDeepResearchHandlerParams): ((update: DeepResearchUpdate) => void) => {
  return (update: DeepResearchUpdate) => {
    if (isStreamCancelled()) return;

    updateMessages(messages => {
      const msgIndex = messages.findIndex(m => m.id === messageId);
      if (msgIndex === -1) return messages;

      const msg = messages[msgIndex];
      const currentContext: DeepResearchContext = msg.deepResearchContext || {
        state: 'IDLE',
        currentStep: null,
        history: [],
        stats: {
          durationMs: 0,
          sourceCount: 0,
          searchCount: 0,
        },
        startTimestamp: now(),
      };

      // Deep copy relevant parts to ensure immutability
      const newContext = {
        ...currentContext,
        stats: { ...currentContext.stats },
        history: [...currentContext.history],
        startTimestamp: currentContext.startTimestamp,
      };

      const { type, data } = update;
      const updatedMessages = [...messages];

      switch (type) {
        case 'start':
          newContext.state = 'ACTIVE';
          // Store start timestamp if not already set
          if (!newContext.startTimestamp) {
            newContext.startTimestamp = now();
          }
          break;

        case 'update': {
          // Auto-activate if we receive updates but missed the start event
          if (newContext.state === 'IDLE') {
            newContext.state = 'ACTIVE';
            if (!newContext.startTimestamp) {
              newContext.startTimestamp = now();
            }
          }
          const { node, data: stepData } = data || {};

          let stepTitle = '';
          let stepDescription = '';

          // Event A: generate_report_plan - AI created research plan with sections
          if (node === 'generate_report_plan') {
            const sections = stepData?.sections || [];
            if (sections.length > 0) {
              const firstSection = sections[0];
              const lastSection = sections[sections.length - 1];
              stepTitle = 'Structuring Research Plan';
              stepDescription = `Identified ${sections.length} key topics to investigate, ranging from "${firstSection.name}" to "${lastSection.name}".`;
            } else {
              stepTitle = 'Structuring Research Plan';
              stepDescription = 'Analyzing topic and creating outline...';
            }
          }
          // Event B: build_section_with_web_research - Core research work
          else if (node === 'build_section_with_web_research') {
            const completedSections = stepData?.completed_sections || [];
            if (completedSections.length > 0) {
              const latestSection =
                completedSections[completedSections.length - 1];
              stepTitle = `Researching: ${latestSection.name || 'Section'}`;
              stepDescription =
                latestSection.description ||
                'Gathering and analyzing information...';
            } else {
              stepTitle = 'Researching';
              stepDescription = 'Gathering and analyzing information...';
            }
          }
          // Event C: gather_completed_sections - Synthesizing data
          else if (node === 'gather_completed_sections') {
            stepTitle = 'Synthesizing Research Data';
            stepDescription = `Compiling findings from ${newContext.stats.sourceCount || 'multiple'} sources into a cohesive report structure.`;
          }
          // Event D: write_final_sections - Writing final output
          else if (node === 'write_final_sections') {
            const completedSections = stepData?.completed_sections || [];
            if (completedSections.length > 0) {
              const latestDraft =
                completedSections[completedSections.length - 1];
              stepTitle = 'Drafting Final Report';
              stepDescription = `Writing section: ${latestDraft.name || 'Final content'}`;
            } else {
              stepTitle = 'Drafting Final Report';
              stepDescription = 'Composing the final response...';
            }
          }
          // Fallback for unknown nodes with completed_sections
          else if (stepData?.completed_sections) {
            const sections = stepData.completed_sections;
            if (Array.isArray(sections) && sections.length > 0) {
              const lastSection = sections[sections.length - 1];
              stepTitle = lastSection.name || 'Processing...';
              stepDescription = lastSection.description || '';
            }
          }
          // Fallback for unknown nodes
          else {
            stepTitle = 'Processing';
            stepDescription = 'Working on your request...';
          }

          // Only update if we have a valid title and it's different from current
          if (stepTitle) {
            const newStep: ThinkingStep = {
              id: `${node || 'step'}-${now()}`,
              node: node || 'unknown',
              title: stepTitle,
              description: stepDescription,
              timestamp: data?.timestamp || currentDate().toISOString(),
            };

            // Only update if it's a new/different step
            if (
              !newContext.currentStep ||
              newContext.currentStep.title !== newStep.title
            ) {
              // Push previous step to history
              if (newContext.currentStep) {
                newContext.history.push(newContext.currentStep);
              }
              newContext.currentStep = newStep;
              newContext.stats.searchCount++;
            }
          }
          break;
        }

        case 'web_sources': {
          // Extract web search sources from the event
          const webSources = data?.web_search_sources || [];
          newContext.stats.sourceCount =
            data?.source_count || webSources.length || 0;

          // Update message with web search sources for View Sources button
          if (webSources.length > 0) {
            const typedWebSources = webSources as WebSearchSource[];
            updatedMessages[msgIndex] = {
              ...msg,
              deepResearchContext: newContext,
              webSearchSources: typedWebSources,
              additional_kwargs: {
                ...msg.additional_kwargs,
                web_search_sources: typedWebSources,
                web_results: typedWebSources, // Legacy field
              },
            };
            return updatedMessages;
          }
          break;
        }

        case 'complete':
          // Mark research as complete and set message status to 'sent'
          newContext.state = 'COMPLETED_COLLAPSED';
          if (newContext.currentStep) {
            newContext.history.push(newContext.currentStep);
            newContext.currentStep = null;
          }

          // Calculate duration if start timestamp exists
          if (newContext.startTimestamp) {
            newContext.stats.durationMs = now() - newContext.startTimestamp;
          } else {
            // Fallback: use first step timestamp if available
            const firstStep =
              newContext.history.length > 0 ? newContext.history[0] : null;
            if (firstStep?.timestamp) {
              const startTime = new Date(firstStep.timestamp).getTime();
              newContext.stats.durationMs = now() - startTime;
            }
          }

          // Update message status to 'sent' to show retry/view sources buttons
          updatedMessages[msgIndex] = {
            ...msg,
            deepResearchContext: newContext,
            status: 'sent',
          };
          return updatedMessages;
      }

      updatedMessages[msgIndex] = {
        ...msg,
        deepResearchContext: newContext,
      };
      return updatedMessages;
    });
  };
};

/**
 * Determines the appropriate error message based on the error content
 * Used for consistent error handling across streaming handlers
 */
export const getStreamErrorMessage = (
  error: Error,
  isWebSearchEnabled: boolean
): string => {
  const errorMessage = error.message;
  let specificError = errorMessage;

  // Check for HTTP 500 error
  if (
    errorMessage.includes('500') ||
    errorMessage.includes('Internal Server Error')
  ) {
    specificError = isWebSearchEnabled
      ? 'No web results found. Try a different query.'
      : 'Stream interrupted due to server error. Please try again.';
  }

  // Check for network failure
  if (
    errorMessage.includes('network') ||
    errorMessage.includes('NetworkError') ||
    errorMessage.includes('fetch') ||
    errorMessage.includes('timeout')
  ) {
    specificError =
      'Stream interrupted due to network issue. Please try again.';
  }

  return specificError;
};
